import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'accent' | 'secondary' | 'tertiary' | 'danger';
type Size = 'md' | 'lg' | 'xl';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
}

// Inline spinner; respects prefers-reduced-motion via the .btn-spinner
// CSS class. Sized to match the surrounding text line-height so it
// doesn't shift layout when it appears.
function Spinner() {
  return (
    <span
      className="btn-spinner"
      aria-hidden="true"
      style={{
        display: 'inline-block',
        inlineSize: '1em',
        blockSize: '1em',
        border: '2px solid currentColor',
        borderInlineEndColor: 'transparent',
        borderRadius: '50%',
        verticalAlign: '-0.15em',
      }}
    />
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block, leftIcon, rightIcon, loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'btn',
        `btn-${variant}`,
        size === 'lg' && 'btn-lg',
        size === 'xl' && 'btn-xl',
        block && 'btn-block',
        loading && 'btn-loading',
        className,
      )}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : leftIcon}
      <span>{children}</span>
      {rightIcon}
    </button>
  );
});
